import type { AnyValue } from "@/lib/rowTypes";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface CareerState {
  user_id: string;
  career_key: string;
  comp_type: "salary" | "hourly" | "base_plus_commission" | "contract";
  annual_income: number;
  hourly_rate: number | null;
  weekly_hours: number | null;
  career_choice_key: string | null;
  side_hustle_key: string | null;
  side_hustle_choice_key: string | null;
  career_fit_score: Record<string, number | string>;
  side_hustle_metrics: Record<string, number | string>;
  activated_at: string;
}

/**
 * Read-only hook: is the learner's Career Path module passed and activated?
 * Used to gate every job/side-hustle event and the daily paycheck flow.
 */
export function useCareerActivation() {
  const { user } = useAuth();
  const [state, setState] = useState<CareerState | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!user) {
      setState(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("learner_career_state")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setState((data as AnyValue) || null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { state, activated: !!state, loading, refetch };
}
